import React, {PropsWithChildren, useState} from "react";
import {Collapse, Grid2 as Grid, IconButton, Typography} from "@mui/material";
import { makeStyles } from "@mui/styles";
import EditLocationOutlined from "@mui/icons-material/EditLocationOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {theme} from "../../../theme";

export type LocationBayProps = {
    locationName: string;
    onLocationNameChange: (locationName: string, sku?: string) => void;
    hasLineItems: boolean;
};

const useStyles = makeStyles(() => ({
    container: {
        marginTop: theme.spacing(1),
    },
}));

/**
 * LocationBay displays a list of line items from the Receive associated with a specific warehouse inbound / put away location.
 * The location is editable per line item, or all line items at that location. 
 * 
 * Nothing is rendered if there are no line items at the location.
 *
 * Props:
 * - locationName (string): The name of the location associated with the line items.
 * - onLocationNameChange (function): A callback function invoked when the user edits the location.
 * - hasLineItems (boolean): Whether there are any line items at this location.
 * - children (React.ReactNode): The children components to be rendered and controlled within the collapse functionality.
 */
export const LocationBay: React.FC<PropsWithChildren<LocationBayProps>> = ({
    locationName,
    onLocationNameChange,
    hasLineItems,
    children,
}) => {
    const classes = useStyles();
    const [open, setOpen] = useState(true);

    return (
        hasLineItems ? (
            <div className={classes.container}>
                <Grid container alignItems="flex-start">
                    <Grid size={{ lg: "grow" }}>
                        <Typography variant="body2" color="textPrimary" display="inline">
                            {locationName ? (
                                `Items assigned to ${locationName}`
                            ) : (
                                "Items not assigned"
                            )}
                        </Typography>
                        {locationName && (
                            <IconButton aria-label="expand row" size="small" onClick={() => onLocationNameChange(locationName)}>
                                <EditLocationOutlined />
                            </IconButton>
                        )}
                    </Grid>
                    <Grid container size="grow" justifyContent="flex-end">
                        <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                    </Grid>
                </Grid>
                <Collapse in={open} timeout="auto" unmountOnExit>
                    {children}    
                </Collapse>
            </div>
        ) : (
            <></>
        )
    );
};
